import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { ENV } from '../../config/env';

export interface Profile {
  id: string;
  displayName: string;
  age: number;
  gender: 'female' | 'male' | string;
  city: string;
  tribe?: string;
  religion?: string;
  relationshipIntent?: string;
  bio?: string;
  interests?: string[];
  photoUrls: string[];
  voiceNoteUrl?: string;
  verified: boolean;
  createdAt: Date;
}

export interface ProfileFilters {
  query: string;
  gender: string;
  tribe: string;
  intent: string;
}

export interface ProfilesState {
  profiles: Profile[];
  loading: boolean;
  error: string | null;
  cursor: string | null;
  hasMore: boolean;
  filters: ProfileFilters;
}

interface ListProfilesResult {
  profiles: Array<Omit<Profile, 'createdAt'> & { createdAt: unknown }>;
  cursor: string | null;
  hasMore: boolean;
}

const listProfilesCallable = httpsCallable<
  { cursor?: string; limit?: number; gender?: string; tribe?: string; intent?: string },
  ListProfilesResult
>(functions, 'listProfiles');

type Listener = (state: ProfilesState) => void;

/** Safely converts Firestore Timestamp payloads (ISO string, {_seconds,_nanoseconds},
 * or {seconds,nanoseconds}) into a Date. Falls back to `new Date()` if the shape is
 * unrecognized, but logs so mismatches with the Cloud Function's serialization are
 * caught early instead of silently producing "Invalid Date" everywhere.
 */
function parseTimestamp(value: unknown): Date {
  if (!value) return new Date();

  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d;
  }

  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    const seconds = (obj._seconds ?? obj.seconds) as number | undefined;
    if (typeof seconds === 'number') {
      return new Date(seconds * 1000);
    }
  }

  console.warn('Unrecognized createdAt format from listProfiles:', value);
  return new Date();
}

class ProfilesStore {
  private state: ProfilesState = {
    profiles: [],
    loading: false,
    error: null,
    cursor: null,
    hasMore: true,
    filters: { query: '', gender: 'all', tribe: 'all', intent: 'all' },
  };

  private listeners: Set<Listener> = new Set();

  /** Incremented on every new request/reset so in-flight responses from a
   * superseded fetch (e.g. rapid filter switching) can be detected and discarded. */
  private requestId = 0;

  get profiles(): Profile[] {
    return this.state.profiles;
  }

  get loading(): boolean {
    return this.state.loading;
  }

  get error(): string | null {
    return this.state.error;
  }

  get hasMore(): boolean {
    return this.state.hasMore;
  }

  get filters(): ProfileFilters {
    // Shallow copy so consumers can't silently mutate internal state
    // without going through setFilters and triggering notify().
    return { ...this.state.filters };
  }

  /**
   * Returns profiles filtered by the current text search query.
   * Gender, tribe, and intent filters are applied server-side via listProfiles
   * and do NOT need client-side re-filtering.
   */
  get filteredProfiles(): Profile[] {
    const search = this.state.filters.query.toLowerCase().trim();
    if (!search) return this.state.profiles;

    return this.state.profiles.filter((p) => {
      const haystack = [p.displayName, p.city || '', p.bio || '', p.tribe || '']
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener({ ...this.state });
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    const snapshot = { ...this.state, filters: { ...this.state.filters } };
    this.listeners.forEach((fn) => fn(snapshot));
  }

  /**
   * Updates client-side filters. Changes to gender, tribe, or intent
   * trigger a full server re-fetch via listProfiles. Text search is
   * client-side only and only re-renders the current page.
   */
  setFilters(filters: Partial<ProfileFilters>): void {
    this.state.filters = { ...this.state.filters, ...filters };

    const hasServerFilter = 'gender' in filters || 'tribe' in filters || 'intent' in filters;

    if (hasServerFilter) {
      this.reset();
      void this.loadProfiles();
      return;
    }

    this.notify();
  }

  /**
   * Fetches profiles from the listProfiles callable (server-side).
   * All server-supported filters — gender, tribe, intent — are forwarded.
   * Blocked users are excluded server-side and never reach the client.
   *
   * Guards against stale in-flight responses via a request token, so
   * rapidly switching filters can never let an old response overwrite
   * newer state (a real race in the previous implementation).
   */
  async loadProfiles(): Promise<void> {
    if (this.state.loading || (!this.state.hasMore && this.state.profiles.length > 0)) return;

    const currentRequest = ++this.requestId;
    this.state.loading = true;
    this.state.error = null;
    this.notify();

    try {
      const { gender, tribe, intent } = this.state.filters;

      const result = await listProfilesCallable({
        cursor: this.state.cursor ?? undefined,
        limit: ENV.pageSize,
        gender: gender !== 'all' ? gender : undefined,
        tribe: tribe !== 'all' ? tribe : undefined,
        intent: intent !== 'all' ? intent : undefined,
      });

      // Discard if superseded by a reset/newer request while awaiting.
      if (currentRequest !== this.requestId) return;

      const { profiles, cursor, hasMore } = result.data;

      const mapped: Profile[] = profiles.map((p) => ({
        ...p,
        createdAt: parseTimestamp(p.createdAt),
      }));

      const existingIds = new Set(this.state.profiles.map((p) => p.id));
      const deduped = mapped.filter((p) => !existingIds.has(p.id));

      this.state.profiles = [...this.state.profiles, ...deduped];
      this.state.cursor = cursor;
      this.state.hasMore = hasMore;
    } catch (err) {
      if (currentRequest !== this.requestId) return;

      console.warn('Failed to load profiles:', err);
      this.state.error =
        err instanceof Error ? err.message : 'Failed to load profiles. Please try again.';

      if (this.state.profiles.length === 0) {
        this.state.hasMore = false;
      }
    } finally {
      if (currentRequest === this.requestId) {
        this.state.loading = false;
        this.notify();
      }
    }
  }

  /** Retries the last failed page load, clearing the error state first. */
  async retry(): Promise<void> {
    this.state.error = null;
    this.notify();
    await this.loadProfiles();
  }

  reset(): void {
    // Bump requestId so any in-flight fetch from before the reset is
    // discarded when it resolves, instead of appending stale data.
    this.requestId++;
    this.state.profiles = [];
    this.state.cursor = null;
    this.state.hasMore = true;
    this.state.loading = false;
    this.state.error = null;
    this.notify();
  }
}

export const profilesStore = new ProfilesStore();